import React, { createContext, useState, useContext } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import StratsRepository from '@/finsight/infrastructure/repository/StratsRepository/StratsRepository';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { initialStratsSidebarData } from './constants';
import { IGroupItem, ISidebarMetadata, TFileId } from '@/finsight/domain/strats/types/SidebarStrats';

export const useStratsSidebar = () => {
  const stratsRepository = useDIContext().container.get(StratsRepository);
  const [metadata, setMetadata] = useState<ISidebarMetadata>(initialStratsSidebarData);
  const [groups, setGroups] = useState<IGroupItem[]>([]);
  const [metadataFetching, setMetadataFetching] = useState<boolean>(false);
  const [sidebarGroupsFetchig, setSidebarGroupsFetchig] = useState<boolean>(false);

  const getSidebarMetadata = async (fileId: TFileId) => {
    setMetadataFetching(true);
    try {
      const response = await stratsRepository.getSidebarMetadata(fileId);
      setMetadata(response);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setMetadataFetching(false);
    }
  };

  const getSidebarGroups = async (fileId: TFileId) => {
    setSidebarGroupsFetchig(true);
    try {
      const { groups } = await stratsRepository.getSidebarGroups(fileId);
      setGroups(groups);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setSidebarGroupsFetchig(false);
    }
  };

  const viewEdgarFiling = () => window.open(metadata?.edgarFilingUrl, '_blank');

  return {
    metadata,
    groups,
    metadataFetching,
    sidebarGroupsFetchig,
    getSidebarMetadata,
    getSidebarGroups,
    viewEdgarFiling,
  };
};

type StratsSidebarContextType = ReturnType<typeof useStratsSidebar>;

export const StratsSidebarContext = createContext<StratsSidebarContextType>(null);

export const useStratsSidebarContext = () => {
  const context = useContext(StratsSidebarContext);
  if (!context) {
    throw new Error('useStratsSidebarContext must be used within the SratsSidebarContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const StratsSidebarContextProvider = ({ children }: IProps) => (
  <StratsSidebarContext.Provider value={ useStratsSidebar() }>
    { children }
  </StratsSidebarContext.Provider>
);

export default StratsSidebarContextProvider;
